import { Dispatch, SetStateAction } from 'react';
import {
  Button,
  ControlGroup,
  Divider,
  FormGroup,
  InputGroup,
} from '@blueprintjs/core';
import { ace_index, permissions } from '@axo/shared/data-access/types';
import { useAvailableClients } from '../../hooks/utils/useAvailableClients';
import { MultiSelect } from '../MultiSelect';
import { WorkingListCard } from './WorkingListCard';
import { CardDivider } from './CardDivider';
import { CardRow } from './CardRow';
import styles from './EditCard.module.css';

interface Props {
  listDetails: ace_index.WorkingList;
  setListDetails: Dispatch<SetStateAction<ace_index.WorkingList>>;
  onCancel: () => void;
  onSave: () => void;
  loading: boolean;
}

export function EditCard({
  listDetails,
  setListDetails,
  onCancel,
  onSave,
  loading,
}: Props) {
  const selectedRoles = currentRoles(listDetails);
  const selectedCountries = currentCountries(listDetails);
  const selectedUsers = currentUsers(listDetails);

  const clients = useAvailableClients();

  return (
    <WorkingListCard className={styles.card}>
      <form
        onSubmit={(e) => {
          e.preventDefault();

          if (isValid(listDetails)) {
            onSave();
          }
        }}
      >
        <ControlGroup>
          <InputGroup
            placeholder="Name"
            autoComplete="off"
            required
            autoFocus
            value={listDetails.Name}
            onChange={(e) => setListDetails(setName(e.target.value))}
          />

          <Divider />

          <Button
            icon="floppy-disk"
            intent="primary"
            minimal
            disabled={!isValid(listDetails)}
            text="Save"
            type="submit"
            loading={loading}
          />

          <Divider />

          <Button
            icon="cross"
            intent="warning"
            minimal
            text="Cancel"
            onClick={onCancel}
            disabled={loading}
          />
        </ControlGroup>

        <CardDivider />

        <CardRow>
          <div className={styles.wrapper}>
            <FormGroup
              label="Country"
              intent={selectedCountries.length < 1 ? 'danger' : 'none'}
              helperText={
                selectedCountries.length < 1
                  ? 'You need to select at least one country'
                  : undefined
              }
            >
              <MultiSelect
                placeholder="Country"
                data={countries}
                values={selectedCountries}
                onSelect={(country) => setListDetails(toggleCountry(country))}
                onRemove={(_, i) =>
                  setListDetails(toggleCountry(selectedCountries[i]))
                }
                stringify={(country) => country}
              />
            </FormGroup>

            <FormGroup label="Role">
              <MultiSelect
                placeholder="Role"
                data={roles}
                values={selectedRoles}
                onSelect={(role) => setListDetails(toggleRole(role))}
                onRemove={(_, i) =>
                  setListDetails(toggleRole(selectedRoles[i]))
                }
                stringify={(role) => role}
              />
            </FormGroup>

            <FormGroup label="User">
              <MultiSelect
                placeholder="User"
                data={clients.map((client) => client.ID)}
                values={selectedUsers}
                onSelect={(user) => setListDetails(toggleUser(user))}
                onRemove={(_, i) =>
                  setListDetails(toggleUser(selectedUsers[i]))
                }
                stringify={(clientID) =>
                  clients.find((client) => client.ID === clientID)
                    ?.DisplayName ?? '?'
                }
                keyify={(clientID) => clientID}
              />
            </FormGroup>
          </div>
        </CardRow>
      </form>
    </WorkingListCard>
  );
}

export const roles = [
  permissions.RoleName.Sales,
  permissions.RoleName.SecuredSales,
  permissions.RoleName.Telemarketing,
  permissions.RoleName.Manager,
  permissions.RoleName.TelemarketingLimited,
];

export function toggleRole(
  role: (typeof roles)[number],
): SetStateAction<ace_index.WorkingList> {
  return (prev) => {
    const next = new Set(prev.Audience.Roles);

    if (next.has(role)) {
      next.delete(role);
    } else {
      next.add(role);
    }

    return {
      ...prev,
      Audience: {
        ...prev.Audience,
        Roles: Array.from(next),
      },
    };
  };
}

export function currentRoles(list: ace_index.WorkingList) {
  return list.Audience.Roles ?? [];
}

export const countries = ['DK', 'FI', 'NO', 'SE'];

export function toggleCountry(
  country: (typeof countries)[number],
): SetStateAction<ace_index.WorkingList> {
  return (prev) => {
    const next = new Set(prev.Audience.MarketCountries);

    if (next.has(country)) {
      next.delete(country);
    } else {
      next.add(country);
    }

    return {
      ...prev,
      Audience: {
        ...prev.Audience,
        MarketCountries: Array.from(next),
      },
    };
  };
}

export function currentCountries(list: ace_index.WorkingList) {
  return list.Audience.MarketCountries ?? [];
}

export function toggleUser(
  clientID: string,
): SetStateAction<ace_index.WorkingList> {
  return (prev) => {
    const next = new Set(prev.Audience.ClientIDs);

    if (next.has(clientID)) {
      next.delete(clientID);
    } else {
      next.add(clientID);
    }

    return {
      ...prev,
      Audience: {
        ...prev.Audience,
        ClientIDs: Array.from(next),
      },
    };
  };
}

export function currentUsers(list: ace_index.WorkingList) {
  return list.Audience.ClientIDs ?? [];
}

function setName(name: string): SetStateAction<ace_index.WorkingList> {
  return (prev) => ({
    ...prev,
    Name: name,
  });
}

function isValid(list: ace_index.WorkingList) {
  if (currentCountries(list).length < 1) {
    return false;
  }

  return true;
}
